import {
    abbreviationMap,
    CompleteUserData,
    Product,
    RecommendedProductsRequest,
    RecommendedProductsResponse,
    stateMap,
} from '@amplify/athena-models';
import {AllProductType} from '../CaseManager/Forms';
import {ParameterStore} from '../Core/mobx/ParameterStore/ParameterStore';
import AthenaAPI from './AthenaAPI';
import CRM, {ClientWalkthroughData} from './CRM';
import API from './API';
import moment from 'moment';
import {sortBy} from 'lodash';

export async function updateRecommendationProducts(email: string, parameterStore: ParameterStore) {
    const {data} = await CRM.getCRMWalkthroughData(email);

    // Store raw walkthrough data for Prosper Flow detection
    (parameterStore as any)._walkthroughRawData = data;

    const qualifiedProducts = await getQualifiedProducts(data, parameterStore);
    parameterStore.qualifiedProducts = qualifiedProducts?.availableProducts;
    parameterStore.recommendedProductsResponse = qualifiedProducts.recommendedProductResponse;
}

export async function getQualifiedProducts(
    data: ClientWalkthroughData,
    parameterStore: ParameterStore | null = null,
): Promise<{availableProducts: AllProductType[]; recommendedProductResponse: RecommendedProductsResponse | null}> {
    const updatedUserData = updateRecommendationData(data?.userData, parameterStore);

    const recommendedProductsRequest: RecommendedProductsRequest = {
        userData: updatedUserData,
        userIdData: data?.userIdData,
        fetchOnly: true,
        includeApOnly: true,
    };

    if (!recommendedProductsRequest.userData || !recommendedProductsRequest.userIdData) {
        return {availableProducts: [], recommendedProductResponse: null};
    }
    const recommendedProductResponse = await API.Call<RecommendedProductsResponse>(
        'v2/recommendation/recommended-products',
        'POST',
        recommendedProductsRequest,
    );

    if (!recommendedProductResponse) {
        throw new Error('Failed to fetch recommended-products');
    }

    const qualifiedProductSet = new Set(recommendedProductResponse.allEligibleProductCodes);

    const availableProducts = sortBy(
        recommendedProductResponse.allProducts.map((p) => ({
            ...p,
            ...(qualifiedProductSet.has(p.code) ? {recommended: true} : {}),
        })),
        [(p) => !p.recommended, (p) => -p.productRank],
    );

    return {availableProducts, recommendedProductResponse};
}

const abbreviationToIdMap = new Map(Array.from(abbreviationMap.entries()).map(([k, v]) => [v, k]));

// Helper function to coerce empty strings to null for boolean fields
const coerceBoolean = (value: any): boolean | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    return Boolean(value);
};

const mappings: [(ps: ParameterStore) => any, keyof CompleteUserData][] = [
    [(ps) => abbreviationToIdMap.get(ps.prequal.state.value), 'stateId'],
    [
        (ps) => {
            // Convert display values to SNAKE_CASE for API
            const value = ps.prequal.usCitizenship.value;
            if (!value) return null;

            // The app converts display values to PascalCase, so we map those to SNAKE_CASE
            const displayToPascalMap: {[key: string]: string} = {
                Yes: 'US_CITIZEN',
                USCitizen: 'US_CITIZEN', // In case already converted
                'Permanent Resident': 'PERMANENT_RESIDENT',
                PermanentResident: 'PERMANENT_RESIDENT', // In case already converted
                'U.S. Visa Holder': 'US_VISA_HOLDER',
                USVisaHolder: 'US_VISA_HOLDER', // In case already converted
                'No U.S. Visa - TIN only': 'NO_US_VISA_TIN_ONLY',
                NoUSVisaTINOnly: 'NO_US_VISA_TIN_ONLY', // In case already converted
            };

            return displayToPascalMap[value] || value;
        },
        'usCitizenship',
    ],
    [
        (ps) => {
            const prequal = ps.prequal;
            if (prequal.bday.value && prequal.bmonth.value && prequal.byear.value) {
                return moment({year: prequal.byear.value, month: prequal.bmonth.value - 1, day: prequal.bday.value});
            }
            return null;
        },
        'dateOfBirth',
    ],
    [(ps) => (ps.prequal.gender.value as string)?.toLowerCase(), 'gender'],
    [
        (ps) => {
            // If tobacco question is unanswered (null, undefined, or empty string), return null
            if (
                ps.prequal.tobacco.value === null ||
                ps.prequal.tobacco.value === undefined ||
                ps.prequal.tobacco.value === ''
            ) {
                return null;
            }
            // If tobacco is explicitly answered as false, return false
            if (ps.prequal.tobacco.value === false) {
                return false;
            }
            // If tobacco is true, check if it's not marijuana
            return ps.prequal.tobacco.value && ps.prequal.tobaccoUsage.value !== 'Marijuana';
        },
        'usedTobacco',
    ],
    [
        (ps) => {
            // If tobacco question is unanswered (null, undefined, or empty string), return null
            if (
                ps.prequal.tobacco.value === null ||
                ps.prequal.tobacco.value === undefined ||
                ps.prequal.tobacco.value === ''
            ) {
                return null;
            }
            // If tobacco is explicitly answered as false, return false
            if (ps.prequal.tobacco.value === false) {
                return false;
            }
            // If tobacco is true, check if it's marijuana
            return ps.prequal.tobacco.value && ps.prequal.tobaccoUsage.value === 'Marijuana';
        },
        'usedMarijuana',
    ],
    [(ps) => ps.prequal.weight.value, 'weightPounds'],
    [(ps) => ps.prequal.height.value, 'heightInches'],
    [(ps) => coerceBoolean(ps.prequal.DrugorAlcoholDependenceWithdrawal.value), 'behaviorDrugAlcoholAbuse'],
    [(ps) => coerceBoolean(ps.prequal.DUI.value), 'behaviorDuiRecklessDriving'],
    [(ps) => coerceBoolean(ps.prequal.License.value), 'behaviorSuspendedLicense'],
    [(ps) => coerceBoolean(ps.prequal.Movingviolations.value), 'behaviorMovingViolation'],
    [(ps) => coerceBoolean(ps.prequal.Bankruptcy.value), 'bankruptcy'],
    [(ps) => coerceBoolean(ps.prequal.HIV.value), 'hivAids'],
    [(ps) => ps.prequal.currentlyEmployed.value, 'currentlyEmployed'],
    [(ps) => coerceBoolean(ps.health.SleepApnea.value), 'sleepApnea'],
    [(ps) => ps.health.sleepapneaSeverity.value, 'sleepApneaLevel'],
    // ENG-4173 - requested below updates
    [(ps) => coerceBoolean(ps.health.Cancer.value), 'cancer'],
    [
        (ps) =>
            ps.health.bipolarHospitalizations.value ||
            ps.health.schizophreniaHospitalizations.value ||
            ps.health.psychHospitalization.value,
        'mentalDisorderHospitalization',
    ],
    [
        (ps) => {
            // Check if any mental disorder conditions are true
            const hasDisorder =
                ps.health.BipolarDisorder.value ||
                ps.health.Schizophrenia.value ||
                ps.health.Anxiety.value ||
                ps.health.Depression.value;
            return coerceBoolean(hasDisorder);
        },
        'diagMentalDisorder',
    ],
    [
        (ps) => {
            // Build array of mental disorder diagnoses
            const diagnoses = [];
            if (ps.health.Schizophrenia.value) diagnoses.push('schizophrenia');
            if (ps.health.Depression.value) diagnoses.push('depression');
            if (ps.health.BipolarDisorder.value) diagnoses.push('bipolar');
            if (ps.health.Anxiety.value) diagnoses.push('anxiety');
            return diagnoses.length > 0 ? diagnoses : null;
        },
        'mentalDisorderDiagnoses',
    ],
    [(ps) => coerceBoolean(ps.prequal.Criminaloffense.value), 'criminalOffense'],
    [
        (ps) => {
            // Convert criminal offense date to ISO string if it exists
            if (ps.prequal.criminalOffenseDate.value) {
                return moment(ps.prequal.criminalOffenseDate.value).toISOString();
            }
            return null;
        },
        'criminalOffenseDate',
    ],
    [(ps) => ps.prequal.bankruptcyOpen.value, 'bankruptcyIsOpen'],
    [(ps) => ps.prequal.suspendedLicense.value, 'suspendedLicenseStill'],
    [
        (ps) =>
            ['Completed', 'Intreatment'].includes(ps.prequal.DrugorAlcoholDependenceWithdrawalTreatmentStatus.value),
        'drugAlcoholTreatment',
    ],
    [
        (ps) =>
            ps.prequal.DrugorAlcoholDependenceWithdrawalTreatmentDate.value
                ? moment(ps.prequal.DrugorAlcoholDependenceWithdrawalTreatmentDate.value).toISOString()
                : null,
        'drugAlcoholTreatmentDate',
    ],
    [(ps) => ps.health.WeightLossObesityGastric.value, 'weightLossSurgery'],
    [(ps) => ps.health.AtrialFibulationAfib.value, 'atrialFibrillation'],
    [(ps) => coerceBoolean(ps.health.Defibrillator.value), 'defibrillator'],
    [(ps) => coerceBoolean(ps.health.Transplant.value), 'organTransplant'],
    [(ps) => ps.health.cancerStage.value, 'cancerStage'],
    [(ps) => ps.prequal.criminalOffenseChargeType.value === 'Felony', 'felonyOffense'],
    [(ps) => ps.prequal.currentDeploymentOrders.value, 'militaryCurrentDeployment'],
    [(ps) => ps.prequal.criminalOffenseProbation.value, 'paroleProbation'],
    [
        (ps) => {
            const value = ps.prequal.maritalStatus.value;
            if (!value) return null;

            // Map to API Values
            const displayToPascalMap: {[key: string]: string} = {
                Single: 'SINGLE',
                Married: 'MARRIED',
                Divorced: 'DIVORCED',
                Separated: 'SEPARATED',
                Widowed: 'WIDOWED',
                'Domestic Partnership': 'DOMESTIC_PARTNERSHIP',
                DomesticPartnership: 'DOMESTIC_PARTNERSHIP', // In case already converted
            };

            return displayToPascalMap[value] || value;
        },
        'maritalStatus',
    ],
    [
        (ps) => {
            // Map employment status from currentlyEmployed field to the API Values
            const status = ps.prequal.currentlyEmployed.value;
            if (!status) return null;

            // map those to API Values
            const statusMap: {[key: string]: string} = {
                Employed: 'EMPLOYED',
                'Homemaker - Spouse Employed': 'HOMEMAKER_SPOUSE_EMPLOYED',
                HomemakerSpouseEmployed: 'HOMEMAKER_SPOUSE_EMPLOYED', // In case already converted
                Unemployed: 'UNEMPLOYED',
                Retired: 'RETIRED',
                Student: 'STUDENT',
                Disability: 'DISABILITY',
                Other: 'OTHER',
                // Legacy uppercase values
                EMPLOYED: 'EMPLOYED',
                HOMEMAKER_SPOUSE_EMPLOYED: 'HOMEMAKER_SPOUSE_EMPLOYED',
                UNEMPLOYED: 'UNEMPLOYED',
                RETIRED: 'RETIRED',
                STUDENT: 'STUDENT',
                DISABILITY: 'DISABILITY',
                OTHER: 'OTHER',
            };

            return statusMap[status] || status;
        },
        'employmentStatus',
    ],
    [(ps) => coerceBoolean(ps.prequal.DUI.value), 'duiLast5years'],
    [
        (ps) => {
            // Convert DUI date to ISO string if it exists
            if (ps.prequal.DUIDate.value) {
                return moment(ps.prequal.DUIDate.value, 'MM/YYYY').toISOString();
            }
            return null;
        },
        'duiDate',
    ],
];

// supplement walkthrough reccommendation data wtih data collected from this app
function updateRecommendationData(userData: CompleteUserData, parameterStore: ParameterStore | null): CompleteUserData {
    if (!parameterStore) {
        return userData;
    }

    let result: CompleteUserData = {
        ...userData,
    };

    for (const [mappingFn, k] of mappings) {
        const value = mappingFn(parameterStore);
        if (value != null) {
            result[k] = value as never;
        }
    }

    return result;
}
